import type { ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { authClient } from '../../lib/auth';
import { useAuth, useSignOut } from './useAuth';

/**
 * Route guard. Note what this is and is not: it decides what to *render*, and
 * nothing more. Every API route is independently authenticated server-side — a
 * user who edits their way past this guard reaches a screen whose queries all
 * return 401.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isAccountNotSetUp, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    // Deliberately blank rather than a spinner: the profile request usually
    // resolves in under 100ms against a warm session, and a flashed spinner reads
    // as jank. A skeleton belongs on the screens themselves.
    return <div className="min-h-[100dvh] bg-surface" aria-busy="true" />;
  }

  if (isAccountNotSetUp) return <AccountNotSetUp />;

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/signin?next=${encodeURIComponent(next)}`} replace />;
  }

  return <>{children}</>;
}

/** Signed in, but no Rundown account. Sending them to /signin would look like the sign-in failed. */
function AccountNotSetUp() {
  const navigate = useNavigate();
  const signOut = useSignOut();
  const email = authClient.getCurrentUser()?.email;

  return (
    <main className="flex min-h-[100dvh] items-center justify-center bg-surface px-4">
      <div className="max-w-sm text-center">
        <img src="/rundown-logo.png" alt="Rundown" className="mx-auto h-12 w-auto" />
        <h1 className="mt-6 text-base font-semibold text-ink">Your account is not set up yet</h1>
        <p className="mt-2 text-sm text-muted">
          {email ? `You signed in as ${email}, but` : 'You signed in, but'} this account has not been added to
          Rundown. Ask your project lead to set it up.
        </p>
        <button
          type="button"
          disabled={signOut.isPending}
          onClick={() =>
            signOut.mutate(undefined, { onSettled: () => void navigate('/signin', { replace: true }) })
          }
          className="mt-4 text-sm font-medium text-accent hover:underline"
        >
          Sign in with a different account
        </button>
      </div>
    </main>
  );
}
